import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

/**
 * BreadcrumbContext: allows child pages (e.g. ProjectDetail) to publish
 * human-readable labels for dynamic route segments (e.g. project IDs).
 *
 * The Breadcrumbs layout component consumes these labels so it can display
 * "Aire - Newstead" instead of "563043c8…".
 */

interface BreadcrumbContextValue {
  /** Map of route segment (e.g. UUID) → human-readable label */
  labels: Record<string, string>;
  /** Register a label for a specific route segment */
  setLabel: (segment: string, label: string) => void;
  /** Remove a label (cleanup on unmount) */
  removeLabel: (segment: string) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  labels: {},
  setLabel: () => {},
  removeLabel: () => {},
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<Record<string, string>>({});

  const setLabel = useCallback((segment: string, label: string) => {
    setLabels((prev) => {
      if (prev[segment] === label) return prev; // no-op avoids re-render
      return { ...prev, [segment]: label };
    });
  }, []);

  const removeLabel = useCallback((segment: string) => {
    setLabels((prev) => {
      if (!(segment in prev)) return prev;
      const next = { ...prev };
      delete next[segment];
      return next;
    });
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ labels, setLabel, removeLabel }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBreadcrumbContext() {
  return useContext(BreadcrumbContext);
}
